import { useEffect, useState, useRef } from "react";
import { Box, Typography, Grid, TextField, Button, IconButton, Snackbar, Alert } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { IWareHouseDetailsDTO } from "../../interfaces/User";
import axios from "axios";
import { API_USER_SERVICE } from "../../config/Constants";
import { getUserDetails, getWarehouseDetails } from "../../services/User";
import { useAuth } from "../../auth/AuthProvider";

interface WarehouseDetailsProps {
  onNext: () => void;
  onBack?: () => void;
}

const WarehouseDetails = ({ onNext, onBack }: WarehouseDetailsProps) => {
  const outsideInputRef = useRef<HTMLInputElement>(null);
  const insideInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const { setLoginUser} = useAuth();
  
  const [formData, setFormData] = useState({ name: "", location: "", latitude: 0, longitude: 0 });
  const [errors, setErrors] = useState<{ name: string; location: string }>({ name: "", location: "" });
  const [outsideFiles, setOutsideFiles] = useState<File[]>([]);
  const [insideFiles, setInsideFiles] = useState<File[]>([]);
  const [outsidePreviews, setOutsidePreviews] = useState<string[]>([]);
  const [insidePreviews, setInsidePreviews] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [warehouseData, setWarehouseData] = useState<any>(null);
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);

  useEffect(() => {
    const fetchWarehouse = async () => {
      try {
        const response = await getWarehouseDetails();
        if (response) {
          const data = response.rawResponse.data;
          setWarehouseData(data);
          setFormData({ name: data.name ?? "", location: data.location ?? "", latitude: data.latitude ?? 0, longitude: data.longitude ?? 0 });
          if (data.outsideImg && Array.isArray(data.outsideImg)) setOutsidePreviews(data.outsideImg);
          if (data.insideImg && Array.isArray(data.insideImg)) setInsidePreviews(data.insideImg);
        }
      } catch (err) {
        console.error("Prefill failed:", err);
      }
    };
    fetchWarehouse();
  }, []);

  useEffect(() => {
    if (!locationInputRef.current || !window.google?.maps?.places) return;
    const ac = new google.maps.places.Autocomplete(locationInputRef.current, { types: ["geocode", "establishment"] });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (place?.geometry?.location) {
        setFormData((prev) => ({
          ...prev,
          location: place.formatted_address || place.name || '',
          latitude: place.geometry!.location!.lat(),
          longitude: place.geometry!.location!.lng(),
        }));
        setErrors((prev) => ({ ...prev, location: "" }));
        setHasChanges(true);
      }
    });
    autocompleteRef.current = ac;
  }, [locationInputRef.current]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: "outside" | "inside") => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    const previews: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        continue;
      }
      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    }

    if (type === "outside") {
      setOutsideFiles(prev => [...prev, ...validFiles]);
      setOutsidePreviews(prev => [...prev, ...previews]);
    } else {
      setInsideFiles(prev => [...prev, ...validFiles]);
      setInsidePreviews(prev => [...prev, ...previews]);
    }
    setHasChanges(true);
  };

  const handleRemoveImage = (type: "outside" | "inside", index: number) => {
    if (type === "outside") {
      setOutsidePreviews(prev => prev.filter((_, i) => i !== index));
      setOutsideFiles(prev => prev.filter((_, i) => i !== index));
      if (outsideInputRef.current) outsideInputRef.current.value = "";
    } else {
      setInsidePreviews(prev => prev.filter((_, i) => i !== index));
      setInsideFiles(prev => prev.filter((_, i) => i !== index));
      if (insideInputRef.current) insideInputRef.current.value = "";
    }
  };

  const validate = (): boolean => {
    const newErrors: { name: string; location: string } = { name: "", location: "" };
    let valid = true;

    if (!formData.name) {
      newErrors.name = "Warehouse name is required";
      valid = false;
    }
    if (!formData.location) {
      newErrors.location = "Location is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      if (warehouseData && !hasChanges) {
        onNext();
        return;
      }

      const warehousePayload = {} as IWareHouseDetailsDTO;
      warehousePayload.name = formData.name;
      warehousePayload.address = formData.location;
      warehousePayload.latitude = formData.latitude;
      warehousePayload.longitude = formData.longitude;
      
      const formDataPayload = new FormData();
      formDataPayload.append("data", new Blob([JSON.stringify(warehousePayload)], { type: "application/json" }));
      outsideFiles.forEach(file => formDataPayload.append("outsideImage", file));
      insideFiles.forEach(file => formDataPayload.append("insideImage", file));

      const token = sessionStorage.getItem("token");
      const path = API_USER_SERVICE +`/warehouse/create`;
      await axios.post(path, formDataPayload, {
        headers: { Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
       },
      });

      const userDetailsRepsonse = await getUserDetails();
      console.log("User details response", userDetailsRepsonse);
      if (userDetailsRepsonse && userDetailsRepsonse.success && userDetailsRepsonse.data.id) {
        console.log("inside if block", userDetailsRepsonse.data);
        setLoginUser(JSON.stringify(userDetailsRepsonse.data));
      }

      onNext();
    } catch (error) {
      console.error("Error submitting warehouse:", error);
      setMsg("Failed to submit warehouse details");
      setOpenError(true);
    }
  };

  const handleSnackbarClose = () => {
    setOpenError(false);
  };
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" }, "&::-webkit-scrollbar-thumb:hover": { background: "#94A3B8" } }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Let's know about your warehouse</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>Please provide some details about your warehouse.</Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography component="label" sx={{ display: "block", fontSize: "14px", fontWeight: 500, mb: 1 }}>What do you call your warehouse?</Typography>
              <TextField fullWidth size="small" placeholder="eg. Central" value={formData.name} onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setHasChanges(true); }} error={!!errors.name} helperText={errors.name} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography component="label" sx={{ display: "block", fontSize: "14px", fontWeight: 500, mb: 1 }}>Where is your warehouse located?</Typography>
              <TextField fullWidth size="small" placeholder="eg. Andhra Pradesh" inputRef={locationInputRef} value={formData.location} onChange={(e) => { setFormData({ ...formData, location: e.target.value }); setHasChanges(true); }} error={!!errors.location} helperText={errors.location} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }} />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography component="label" sx={{ display: "block", fontSize: "14px", fontWeight: 500, mb: 1 }}>Outside Warehouse <span style={{ color: "#d32f2f" }}>*</span></Typography>
              <Box onClick={() => outsideInputRef.current?.click()} sx={{ height: "60px", borderRadius: "6px", border: "1px solid rgba(74,85,101,0.25)", backgroundColor: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", cursor: "pointer", transition: "0.2s", "&:hover": { borderColor: "#000", backgroundColor: "#F3F4F6" } }}>
                <AddPhotoAlternateOutlinedIcon fontSize="small" sx={{ color: "grey.primary" }} />
              </Box>
              <input ref={outsideInputRef} type="file" accept="image/png, image/jpeg" multiple hidden onChange={(e) => handleFileChange(e, "outside")} />
              <Typography sx={{ fontSize: 11, color: "#6B7280", mt: 0.5 }}>Max file size: 5 MB · JPG & PNG only</Typography>
              {outsidePreviews.map((preview, index) => (
                <Box key={index} sx={{ mt: 1.5, position: "relative", width: 80, height: 80, borderRadius: "6px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
                  <Box component="img" src={preview} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <IconButton onClick={() => handleRemoveImage("outside", index)} size="small" sx={{ position: "absolute", top: 2, right: 2, color: "#EF4444", padding: "2px", "&:hover": { color: "#DC2626" } }}>
                    <CloseOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography component="label" sx={{ display: "block", fontSize: "14px", fontWeight: 500, mb: 1 }}>Inside Warehouse <span style={{ color: "#d32f2f" }}>*</span></Typography>
              <Box onClick={() => insideInputRef.current?.click()} sx={{ height: "60px", borderRadius: "6px", border: "1px solid rgba(74,85,101,0.25)", backgroundColor: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", cursor: "pointer", transition: "0.2s", "&:hover": { borderColor: "#000", backgroundColor: "#F3F4F6" } }}>
                <AddPhotoAlternateOutlinedIcon fontSize="small" sx={{ color: "grey.primary" }} />
              </Box>
              <input ref={insideInputRef} type="file" accept="image/png, image/jpeg" multiple hidden onChange={(e) => handleFileChange(e, "inside")} />
              <Typography sx={{ fontSize: 11, color: "#6B7280", mt: 0.5 }}>Max file size: 5 MB · JPG & PNG only</Typography>
              {insidePreviews.map((preview, index) => (
                <Box key={index} sx={{ mt: 1.5, position: "relative", width: 80, height: 80, borderRadius: "6px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
                  <Box component="img" src={preview} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <IconButton onClick={() => handleRemoveImage("inside", index)} size="small" sx={{ position: "absolute", top: 2, right: 2, color: "#EF4444", padding: "2px", "&:hover": { color: "#DC2626" } }}>
                    <CloseOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{ py: 2, px: { xs: 2, md: 3.5 }, backgroundColor: "background.paper", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        {/* <Button variant="text" disabled sx={{ textTransform: "none", color: "#9AA0AF" }}>Skip</Button> */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={onBack} disabled sx={{ borderRadius: "6px", textTransform: "none", color: "#000", border: "1px solid #000" }}>Back</Button>
          <Button variant="contained" onClick={handleSubmit} endIcon={<ArrowForwardIcon />} sx={{ borderRadius: "6px", background: "#005EFF", color: "#fff", textTransform: "none" }}>Create starter layout</Button>
        </Box>
      </Box>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackbarClose}>
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
      </Snackbar>
    </Box>
  );
};
export default WarehouseDetails;
